// src/pages/SpaceSentinelHome.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faBookOpen, faUsers, faChartLine, faShieldAlt, faStar, faNewspaper, faTrophy, faGamepad, faRobot } from '@fortawesome/free-solid-svg-icons';

const SpaceSentinelHome = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "Explorer";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Stellar Learning");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/"); 
  };

  const groupNames = [
    { label: "Stellar Learning", icon: faBookOpen, description: "Immersive books & mags" },
    { label: "Cosmic Productivity", icon: faChartLine, description: "Tools & workflows" },
    { label: "Orbit Kaizen", icon: faRocket, description: "Improvement & rankings" },
    { label: "Astro Earnings", icon: faStar, description: "Earnings & funding" },
    { label: "Galactic Guild", icon: faUsers, description: "Community & innovation" },
    { label: "Nebula Marketplace", icon: faShieldAlt, description: "Projects & defense tech" },
    { label: "StarConnect", icon: faNewspaper, description: "Engagement & news" },
    { label: "Sentinel Ascent", icon: faTrophy, description: "Rewards & growth" },
  ];

  
  const menuButtons1 = [
    // Stellar Learning (adapted from Learning & Development: books, monthly, shorts, trainings, podcast)
    { label: "Immersive Book Platform", icon: faBookOpen, link: "/spacesentinel-immersivebooks", group: "Stellar Learning" },
    { label: "Monthly Space Magazine", icon: faNewspaper, link: "/spacesentinel-monthlymag", group: "Stellar Learning" }, // New: Immersive digital mag
    { label: "Astro Shorts", icon: "🛸", link: "/spacesentinel-astrosnews", group: "Stellar Learning" }, // Adapted from HORECA Shorts
    { label: "Stellar Workshops", icon: "🌌", link: "/spacesentinel-workshops", group: "Stellar Learning" }, // Adapted from Trainings
    { label: "Cosmic Podcast", icon: "🎙️", link: "/spacesentinel-podcast", group: "Stellar Learning" }, // Adapted
    { label: "Orbit Library", icon: "📚", link: "/spacesentinel-library", group: "Stellar Learning" }, // New: Book shelf equivalent

    // Cosmic Productivity (adapted from Owner Productivity: yearbook, deep thinking, goals, kanban, etc.)
    { label: "Star Journal", icon: "📔", link: "/spacesentinel-starjournal", group: "Cosmic Productivity" }, // Adapted from Year Book
    { label: "Nebula Analysis", icon: "🧩", link: "/spacesentinel-nebulaanalysis", group: "Cosmic Productivity" }, // Adapted from DeepThinking
    { label: "Mission Goals", icon: "🎯", link: "/spacesentinel-missiongoals", group: "Cosmic Productivity" }, // Adapted
    { label: "Orbit Kanban", icon: "🗂️", link: "/spacesentinel-kanban", group: "Cosmic Productivity" }, // Adapted
    { label: "Stellar Capture", icon: "🤖", link: "/spacesentinel-stellarcapture", group: "Cosmic Productivity" }, // Adapted from LLM Capture

    // Orbit Kaizen (adapted from HORECA Kaizen: rankings, scorecard, database)
    { label: "Sentinel Ranking", icon: "🏆", link: "/spacesentinel-ranking", group: "Orbit Kaizen" }, // Adapted
    { label: "Orbit Scorecard", icon: "📈", link: "/spacesentinel-scorecard", group: "Orbit Kaizen" }, // Adapted
    { label: "Cosmic Database", icon: "💾", link: "/spacesentinel-database", group: "Orbit Kaizen" }, // Adapted

    // Astro Finance (adapted from Finance & Compliance: funding, value share; added earnings)
    // { label: "Astro Funding", icon: "💰", link: "/spacesentinel-funding", group: "Astro Finance" }, // Adapted
    { label: "StarShare Estimator", icon: "⭐", link: "/spacesentinel-starshare", group: "Astro Finance" }, // Adapted from ValueShare
    { label: "Monthly Earnings", icon: "💸", link: "/spacesentinel-earnings", group: "Astro Finance" }, // New: Digital earning options
    { label: "Scholarships", icon: "🎓", link: "/spacesentinel-scholarships", group: "Astro Finance" }, // New: Rewards scholarship

    // Galactic Guild (adapted from People & Innovation: counseling, consultants, innovations; added teachers contact)
    { label: "Star Counselors", icon: "🧠", link: "/spacesentinel-counselors", group: "Galactic Guild" }, // Adapted; Teachers contact
    { label: "AI StarGuard", icon: faRobot, link: "/spacesentinel-aicounsel", group: "Galactic Guild" }, // New: AI Counselling
    { label: "Guild Innovations", icon: "💡", link: "/spacesentinel-innovations", group: "Galactic Guild" }, // Adapted
    { label: "Design Orbits", icon: "📊", link: "/spacesentinel-designorbits", group: "Galactic Guild" }, // Adapted from DOE

    // Nebula Marketplace (adapted from Business: marketplace, services; added projects, defense tech)
    { label: "Project Marketplace", icon: "🛒", link: "/spacesentinel-marketplace", group: "Nebula Marketplace" }, // Adapted; Showcase projects
    { label: "Launchpad Services", icon: "🚀", link: "/spacesentinel-launchpad", group: "Nebula Marketplace" }, // New
    { label: "Defense Sentinel", icon: faShieldAlt, link: "/spacesentinel-defense", group: "Nebula Marketplace" }, // New: Defense Tech integration
    { label: "Orbital Tenders", icon: "📑", link: "/spacesentinel-tenders", group: "Nebula Marketplace" }, // Adapted

    // StarConnect (adapted from Engagement: forum, games, CSAT)
    { label: "Community Connects", icon: faUsers, link: "/spacesentinel-community", group: "StarConnect" }, // New: LinkedIn-style
    { label: "POV Galaxy", icon: "🗣️", link: "/spacesentinel-povs", group: "StarConnect" }, // New: POVs
    { label: "Presentation Orbit", icon: "📽️", link: "/spacesentinel-presentations", group: "StarConnect" }, // New: Community presentations
    { label: "Cosmic Games", icon: faGamepad, link: "/spacesentinel-games", group: "StarConnect" }, // Adapted
    { label: "Nova News", icon: faNewspaper, link: "/spacesentinel-novanews", group: "StarConnect" }, // New: Short news like inShort

    // Sentinel Ascent (adapted from HORECA Adoption: ladder, stakeholders, advisor; added quizzes/prizes)
    { label: "Ascent Ladder", icon: "🪜", link: "/spacesentinel-ladder", group: "Sentinel Ascent" }, // Adapted
    { label: "Star Stakeholders", icon: "👥", link: "/spacesentinel-stakeholders", group: "Sentinel Ascent" }, // Adapted
    { label: "Sentinel Advisor", icon: "🤝", link: "/spacesentinel-advisor", group: "Sentinel Ascent" }, // Adapted
    { label: "Cosmic Quizzes", icon: "❓", link: "/spacesentinel-quizzes", group: "Sentinel Ascent" }, // New: Quizzes
    { label: "Prize Nebula", icon: faTrophy, link: "/spacesentinel-prizes", group: "Sentinel Ascent" }, // New: Prizes/Rewards
  ];

  const filteredButtons = menuButtons1.filter((btn) => btn.group === activeTab);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-black via-gray-900 to-blue-950 text-gray-100 font-sans overflow-x-hidden">
      {/* Enhanced header with space theme */}
      <header className="sticky top-0 z-50 bg-gray-900/80 backdrop-blur-md border-b border-blue-800/30 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex flex-col">
          <div className="text-2xl font-bold text-blue-400 drop-shadow-lg">SpaceSentinel</div>
          <div className="text-xs text-gray-300">Explore the Cosmos: Learning, Innovation, Defense</div>
          <div className="text-xs text-purple-300">Where Stars Align Skills with Galactic Productivity</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-sm text-blue-300">🌟 {userName}</div>
              <button
                onClick={() => navigate("/spacesentinel-dashboard")}
                className="p-2 rounded-full bg-gray-800/50 hover:bg-blue-700/70 transition-all backdrop-blur-sm"
                title="Dashboard"
              >
                <FontAwesomeIcon icon={faRocket} className="text-blue-400" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-full bg-red-600/80 hover:bg-red-700/90 transition-all"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 rounded-full border border-blue-500/50 text-sm text-blue-200 hover:bg-blue-900/50 transition backdrop-blur-sm"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-4 py-2 rounded-full bg-blue-600/80 text-gray-900 text-sm hover:bg-blue-500/90 transition backdrop-blur-sm font-medium"
              >
                Join Orbit
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger with icon */}
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-2xl text-blue-400 rounded-full bg-gray-800/50 hover:bg-blue-700/30 transition"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
      </header>

      {/* Mobile menu: Enhanced with space links */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900/95 border-b border-blue-800/30 px-4 py-4 backdrop-blur-md">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3 mb-4">
              <div className="text-blue-400 font-medium text-lg">Welcome, {userName} 🚀</div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate("/spacesentinel-dashboard")}
                  className="p-3 rounded-full bg-gray-800/50 hover:bg-blue-700/70 transition"
                >
                  <FontAwesomeIcon icon={faRocket} className="text-blue-400" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-3 rounded-full bg-red-600/80 hover:bg-red-700/90 transition"
                >
                  ⏻
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6 mb-4">
              <Link to="/login" className="text-blue-400 font-medium">Login</Link>
              <Link to="/signup" className="text-blue-400 font-medium">Join Orbit</Link>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
            <Link to="/spacesentinel-insights" className="hover:text-purple-400 transition">🌌 About SpaceSentinel</Link>
            <Link to="/litenshort" className="hover:text-purple-400 transition">📰 Quick Stars</Link>
            <Link to="/spacesentinel-faq" className="hover:text-purple-400 transition">❓ Stellar FAQ</Link>
          </div>
        </div>
      )}

      {/* Top quick links: Space-themed */}
      <div className="bg-blue-900/50 text-white text-xs py-2 backdrop-blur-sm border-b border-blue-800/30">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-4">
          <Link to="/spacesentinel-insights" className="hover:text-yellow-300 transition">🌌 About</Link>
          <Link to="/litenshort" className="hover:text-yellow-300 transition">📰 Nova News</Link>
          <Link to="/spacesentinel-faq" className="hover:text-yellow-300 transition">❓ FAQ</Link>
        </div>
      </div>

      {/* Tabs: Enhanced with icons, responsive grid (3-8 cols based on screen), descriptions on hover */}
      <div className="mt-2 px-4 py-3 bg-gray-900/50 border-b border-blue-800/30">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2 place-items-center">
          {groupNames.map((group) => (
            <button
              key={group.label}
              onClick={() => setActiveTab(group.label)}
              className={`flex flex-col items-center justify-center w-full px-3 py-3 rounded-lg text-xs font-medium transition-all backdrop-blur-sm shadow-md hover:scale-105 ${
                activeTab === group.label
                  ? "bg-blue-700/80 text-yellow-300 border-2 border-yellow-400"
                  : "bg-gray-800/60 text-gray-200 hover:bg-blue-700/40"
              }`}
              title={group.description}
              aria-pressed={activeTab === group.label}
            >
              <FontAwesomeIcon icon={group.icon} className="text-lg mb-1" />
              <span className="text-center leading-tight">{group.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main content: Responsive grid buttons with icons, hover effects, break-words fixed */}
      <main className="flex-grow mx-auto px-4 py-4 w-full max-w-7xl">
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 place-items-center`}>
          {filteredButtons.length === 0 ? (
            <div className="col-span-full text-center text-gray-400 py-8">
              <FontAwesomeIcon icon={faRocket} className="text-4xl text-gray-600 mb-2 mx-auto" />
              <p>Charting new stars... No items yet in this orbit.</p>
            </div>
          ) : (
            filteredButtons.map((btn, idx) => (
              <Link
                key={`${btn.label}-${idx}`}
                to={btn.link}
                className="flex flex-col items-center justify-center w-full px-2 py-3 rounded-lg text-xs font-medium transition-all bg-gray-800/60 hover:bg-blue-700/70 hover:text-yellow-300 hover:scale-105 text-gray-100 text-center shadow-md backdrop-blur-sm group min-h-[100px]"
                title={btn.label}
              >
                <span className="text-xl mb-2 group-hover:animate-bounce">
                  {typeof btn.icon === 'object' ? <FontAwesomeIcon icon={btn.icon} /> : btn.icon}
                </span>
                <span className="break-words leading-tight max-w-[120px]">{btn.label}</span>
              </Link>
            ))
          )}
        </div>
      </main>

      {/* Footer: Updated year, space-themed links */}
      <footer className="bg-gray-900/80 border-t border-blue-800/30 text-gray-400 py-6 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-center md:text-left">© 2025 Think5Whys Global DeepTech Solutions. All rights reserved. 🌌</div>
          <div className="flex flex-wrap gap-4 text-sm justify-center md:justify-end">
            <Link to="/about-us" className="hover:text-blue-400 transition">About</Link>
            <Link to="/privacy-policy" className="hover:text-blue-400 transition">Privacy</Link>
            <Link to="/terms-of-use" className="hover:text-blue-400 transition">Terms</Link>
            <Link to="/contact-us" className="hover:text-blue-400 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SpaceSentinelHome;